<template>
  <section class="network-case-library" :aria-busy="loading">
    <nav class="network-library-crumb" aria-label="当前位置">
      <router-link to="/">首页</router-link><span>/</span><strong>智慧城市网络化管理平台</strong><span>/</span><span>实训案例库</span>
    </nav>
    <operation-message :value="message" type="error" @consumed="message = ''" />
    <div v-if="loading" class="panel empty">正在加载内置案例…</div>
    <template v-else-if="cases.length">
      <div class="filter-bar">
        <input v-model.trim="keyword" class="filter-input" placeholder="搜索案例名称、编码或类型" aria-label="搜索内置案例">
<!--        <select v-model="caseVersion" class="field" aria-label="案例版本"><option value="">全部版本</option><option v-for="version in caseVersions" :key="version" :value="version">v{{ version }}</option></select>-->
        <button class="btn" @click="keyword = ''; caseVersion = ''">重置筛选</button>
      </div>
      <p class="network-library-mode-note">内置案例面向所有角色开放，可不限时间、次数自由练习。每次提交后生成练习报告，练习记录不计入教师分配的正式实训成绩。</p>
      <section class="network-library-panel" data-library-group="builtin">
        <header class="network-library-panel-header"><h2>内置实训案例库</h2><span>共 {{ cases.length }} 套内置案例</span></header>
        <div class="network-library-panel-body">
          <p v-if="!filteredCases.length" class="network-library-mode-note">当前筛选条件下没有匹配的案例。</p>
          <div class="network-training-cards">
            <article v-for="item in visibleCases" :key="item.id" class="network-training-card" :data-case-code="item.code">
              <case-library-banner platform="network" :category="item.type" :theme-index="cases.indexOf(item)" />
              <div class="network-training-title-row"><h3>{{ item.name }}</h3><div class="network-training-badges"><span class="network-training-tag category">{{ item.type }}</span><span class="network-training-tag">内置</span></div></div>
              <p class="network-training-description">{{ item.description }}</p>
              <p class="network-training-meta">{{ item.difficulty }} · 建议 {{ item.hours }} 学时 · {{ item.code || '编码未记录' }} · {{ item.versionNo == null ? '版本未记录' : 'v' + item.versionNo }}</p>
              <div class="network-training-counts">案件数量：<b>{{ item.incidentCount }}</b> 个 · 参考网格：<b>{{ item.refGridCount }}</b> 个</div>
              <div class="network-training-progress">
                <span class="network-training-tag" :class="active(item) ? 'running' : history(item).length ? 'finished' : ''">{{ active(item) ? '练习中' : history(item).length ? '已完成' : '未练习' }}</span>
                <span v-if="active(item)">进度 {{ stageText(active(item).stage) }}</span>
                <span v-else-if="latestReport(item) && latestReport(item).score">得分 {{ scoreText(latestReport(item).score) }}</span>
              </div>
              <div class="network-training-actions">
                <button class="btn small primary" :disabled="!!busy" @click="enter(item)">{{ busy === item.id ? '正在进入…' : active(item) ? '继续练习' : history(item).length ? '再次练习' : '开始练习' }}</button>
                <button v-if="latestReport(item)" class="btn small" :disabled="!!busy" @click="openRecord(latestReport(item), true)">练习报告</button>
                <button v-if="history(item).length" class="btn small" :disabled="!!busy" @click="selectedCase = item">我的练习记录（{{ history(item).length }}）</button>
              </div>
            </article>
          </div>
          <list-pagination v-if="filteredCases.length" :page="currentPage" :page-size="pageSize" :total="filteredCases.length" @change="currentPage = $event" />
        </div>
      </section>
    </template>
    <div v-else class="panel empty"><p>暂未加载到内置案例，请检查后端服务及数据初始化状态。</p><button class="btn" @click="load">重新加载</button></div>
    <lab-dialog :visible="!!selectedCase" :title="selectedCase ? selectedCase.name + ' · 我的练习记录' : '我的练习记录'" size="wide" class="city-lab" @close="selectedCase = null">
      <p class="lab-hint">每次练习独立保存。未完成的练习可继续操作，已完成的练习可查看过程和报告。</p>
      <div v-if="selectedCase" class="lab-table-wrap"><table class="lab-table"><thead><tr><th>练习次数</th><th>开始时间</th><th>完成时间</th><th>状态</th><th>得分</th><th>操作</th></tr></thead><tbody>
        <tr v-for="row in history(selectedCase)" :key="row.runId"><td>第 {{ row.roundNo }} 次</td><td>{{ time(row.startedAt) }}</td><td>{{ time(row.finishedAt) }}</td><td>{{ statusText(row.status) }}</td><td>{{ row.score ? scoreText(row.score) : '—' }}</td><td><div class="lab-button-row"><button class="btn small" @click="openRecord(row, false)">查看过程</button><button v-if="row.reportId" class="btn small" @click="openRecord(row, true)">查看报告</button></div></td></tr>
      </tbody></table></div>
      <template #footer><button class="btn small" @click="selectedCase = null">关闭</button></template>
    </lab-dialog>
  </section>
</template>

<script>
import LabDialog from '../components/NetworkLabDialog.vue'
import CaseLibraryBanner from '../components/CaseLibraryBanner.vue'
import ListPagination from '../components/ListPagination.vue'
import { api, errorText } from '../services/api'

export default {
  name: 'NetworkCaseLibrary',
  components: { LabDialog, CaseLibraryBanner, ListPagination },
  data () { return { keyword: '', caseVersion: '', currentPage: 1, pageSize: 9, loading: true, cases: [], busy: '', message: '', selectedCase: null } },
  computed: {
    caseVersions () { return [...new Set(this.cases.map(i => i.versionNo).filter(v => v != null).map(String))].sort((a, b) => Number(a) - Number(b)) },
    filteredCases () { return this.cases.filter(i => [i.name, i.code, i.type, i.description].join(' ').toLowerCase().includes(this.keyword.toLowerCase()) && (!this.caseVersion || String(i.versionNo) === this.caseVersion)) },
    visibleCases () { const start = (this.currentPage - 1) * this.pageSize; return this.filteredCases.slice(start, start + this.pageSize) }
  },
  created () { this.load() },
  watch: {
    keyword () { this.currentPage = 1 },
    caseVersion () { this.currentPage = 1 },
    cases () { this.ensurePage() }
  },
  methods: {
    async load () { this.loading = true; this.message = ''; try { const result = await api.get('/network/training-library'); this.cases = result.cases || []; this.ensurePage() } catch (error) { this.message = errorText(error) } finally { this.loading = false } },
    ensurePage () { const totalPages = Math.max(1, Math.ceil(this.filteredCases.length / this.pageSize)); if (this.currentPage > totalPages) this.currentPage = totalPages },
    history (item) { return item.practiceHistory || [] },
    active (item) { return this.history(item).find(row => ['IN_PROGRESS', 'PAUSED'].includes(row.status)) },
    latestReport (item) { return this.history(item).find(row => row.reportId) },
    async enter (item) {
      if (this.busy) return
      this.busy = item.id
      try {
        const run = await api.post('/network/training-library/' + encodeURIComponent(item.id) + '/practice', {})
        await this.$router.push({ name: 'network-run', params: { runId: run.id }, query: { practice: '1' } })
      } catch (error) { this.message = errorText(error) } finally { this.busy = '' }
    },
    openRecord (row, report) { this.$router.push({ name: 'network-run', params: { runId: row.runId }, query: report ? { practice: '1', tab: 'report' } : { practice: '1' } }) },
    time (value) { return value ? value.slice(0, 19).replace('T', ' ') : '—' },
    stageText (stage) { return { PLANNING: '规划阶段', RUNNING: '运行阶段' }[stage] || '进行中' },
    statusText (state) { return { IN_PROGRESS: '练习中', PAUSED: '已暂停', SUBMITTED: '已完成', ENDED: '已结束', EXPIRED: '已结束' }[state] || state },
    scoreText (score) { return typeof score === 'object' ? (score.total == null ? '—' : score.total) : score }
  }
}
</script>
